import { TechnicalError } from '@orus.eu/error'

declare global {
  var ORUS_LOCAL_DEV_ENABLE_TRACKING: string | undefined
  var __ORUS_LOCAL_DEV_ENABLE_TRACKING__: boolean
  var __ORUS_LOCAL_DEV_PROJECT_FLAG__: string | undefined
}

export function isProductionEnvironment(): boolean {
  return getEnvironmentName() === 'production'
}

export function isReviewEnvironment(): boolean {
  const environmentName = getEnvironmentName()
  return environmentName !== 'dev' && environmentName !== 'production'
}

export function isReviewOrProductionEnvironment(): boolean {
  return isProductionEnvironment() || isReviewEnvironment()
}

export function isE2eEnvironment(): boolean {
  return getEnvironmentName() === 'e2e'
}

export function isLocalDevEnvironment(): boolean {
  return getEnvironmentName() === 'dev'
}

/**
 * Determines if tracking (analytics, GTM, Google Ads, etc.) should be enabled in local development.
 *
 * This function checks two conditions:
 * 1. The environment is local development (localhost)
 * 2. The tracking flag `__ORUS_LOCAL_DEV_ENABLE_TRACKING__` is explicitly set to true
 *
 * By default, tracking is disabled in development to avoid polluting analytics data.
 * Developers can enable it when working on tracking-related features by setting the flag.
 *
 * @returns {boolean} True if both conditions are met, false otherwise
 */
export function isTrackingEnabledInLocalDev(): boolean {
  const isDevEnv = isLocalDevEnvironment()
  const trackingFlag = __ORUS_LOCAL_DEV_ENABLE_TRACKING__

  return isDevEnv && trackingFlag
}

export function isReviewEnvForProject(stringToCheck: string): boolean {
  return getEnvironmentName() === stringToCheck
}

export function isLocalDevWithProjectFlagEnabled(stringToCheck: string): boolean {
  const isDevEnv = isLocalDevEnvironment()
  return isDevEnv && __ORUS_LOCAL_DEV_PROJECT_FLAG__ === stringToCheck
}

export function getFaviconPath(): string {
  if (isLocalDevEnvironment()) {
    return 'favicon-dev-32x32.png'
  }

  if (isReviewEnvironment() && !isE2eEnvironment()) {
    return 'favicon-staging-32x32.png'
  }

  return 'favicon-32x32.png'
}

export function getEnvironmentName(): string {
  const { origin } = window.location

  if (origin === 'https://app.orus.eu') {
    return 'production'
  }

  if (origin === 'https://app.orustest.eu') {
    return 'staging'
  }

  if ('isOrusEndToEndTestsContext' in window) {
    return 'e2e'
  }

  if (origin.startsWith('http://localhost') || origin.endsWith('.localhost.orustest.eu')) {
    return 'dev'
  }

  const reviewEnvName = extractReviewEnvironmentName(origin)
  if (reviewEnvName) {
    return reviewEnvName
  }

  throw new TechnicalError('Could not detect environment')
}

function extractReviewEnvironmentName(originString: string): string | undefined {
  const start = 'https://'
  const end = '.orustest.eu'
  return originString.startsWith(start) && originString.endsWith(end)
    ? originString.substring(start.length, originString.length - end.length)
    : undefined
}
